import * as React from 'react';

import { colors } from '@ovpn-ui/styles';
import { Help } from '@ovpn-ui/icons';
import { Text, Card, Skeleton, Tooltip, TooltipProps } from '@ovpn-ui/core';

import { EntityInfoCardsWrapper } from './EntityInfoCards.styles';

const CardContext = React.createContext({ id: '', hideEmpty: true });

export function EntityCard({
    label,
    content,
    tooltip,
    tooltipProps = {},
    id,
    style,
    className,
}: {
    id?: string;
    label: string;
    tooltip?: React.ReactNode;
    tooltipProps?: Partial<Omit<TooltipProps, 'trigger'>>;
    content: React.ReactNode;
    style?: React.CSSProperties | undefined;
    className?: string;
}) {
    const { id: parentId, hideEmpty } = React.useContext(CardContext);
    const aqaId = id ?? label.toLowerCase().split(' ').join('-');

    if (!content && typeof content !== 'number' && hideEmpty) {
        return null;
    }

    return (
        <Card style={style} className={className}>
            <Text
                variant="textSm"
                weight="medium"
                color={colors.neutralTextSection}
                className="card-title"
            >
                {label}{' '}
                {tooltip && (
                    <Tooltip trigger={<Help />} {...tooltipProps}>
                        {tooltip}
                    </Tooltip>
                )}
            </Text>
            <Text
                m={0}
                fullWidth
                inline={false}
                variant="displayMd"
                weight="semiBold"
                color={colors.neutralTextTitle}
            >
                <span id={`${parentId}-${aqaId}`}>{content}</span>
            </Text>
        </Card>
    );
}

function Loader({
    template,
}: {
    /**
     * @example "3 2 4" will render 3 rows of skeletons.
     *
     * First row will have 3 skeletons,
     * 2nd - 2 and 3rd - 4.
     */
    template: string;
}) {
    const skeletons = React.useMemo(() => {
        const parts = template.split(' ').map(t => Number(t));

        return parts.map((len, i) => (
            <div key={i} className="row">
                {new Array(len).fill(0).map((_t, j) => (
                    <Skeleton key={j} variant="rect" fullWidth height={110} />
                ))}
            </div>
        ));
    }, [template]);

    return <EntityInfoCardsWrapper>{skeletons}</EntityInfoCardsWrapper>;
}

function EntityInfoCardsInteral({
    id,
    className,
    hideEmpty = true,
    children,
}: {
    id: string;
    className?: string;

    /**
     * Don't render card if its content is falsy.
     *
     * True by default
     */
    hideEmpty?: boolean;

    children: React.ReactNode;
}) {
    const ctx = React.useMemo(() => ({ id, hideEmpty }), [id, hideEmpty]);

    return (
        <EntityInfoCardsWrapper id={id} className={className}>
            <CardContext.Provider value={ctx}>{children}</CardContext.Provider>
        </EntityInfoCardsWrapper>
    );
}

export const EntityInfoCards = Object.assign(EntityInfoCardsInteral, {
    /**
     * Use to render a loader of rect skeletons
     * while your card data is loading.
     */
    Loader,
});
